import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../utils/api';

const DashboardPage = () => {
    const { organizationId } = useParams();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(organizationId);

    useEffect(() => {
        loadOrganizations();
    }, []);

    useEffect(() => {
        if (selectedOrgId) {
            loadStats();
        }
    }, [selectedOrgId]);

    const loadOrganizations = async () => {
        try {
            const response = await apiRequest('/organizations/my');
            setOrganizations(response.data || []);
            if (response.data && response.data.length > 0 && !selectedOrgId) {
                setSelectedOrgId(response.data[0].id);
            }
        } catch (error) {
            console.error('Load organizations error:', error);
        }
    };

    const loadStats = async () => {
        if (!selectedOrgId) return;
        
        setLoading(true);
        try {
            // Load statistics from various endpoints
            const [restaurants, categories, products, campaigns, votes, messages, analytics] = await Promise.all([
                apiRequest(`/organizations/${selectedOrgId}/restaurants`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/categories`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/products`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/campaigns`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/votes`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/contact`).catch(() => ({ data: [] })),
                apiRequest(`/organizations/${selectedOrgId}/analytics/statistics`).catch(() => ({ data: {} }))
            ]);

            setStats({
                restaurants: restaurants.data?.length || 0,
                categories: categories.data?.length || 0,
                products: products.data?.length || 0,
                campaigns: campaigns.data?.length || 0,
                votes: votes.data?.length || 0,
                messages: messages.data?.length || 0,
                unreadMessages: messages.data?.filter(m => m.status === 'unread').length || 0,
                totalHits: analytics.data?.total || 0,
                uniqueVisitors: analytics.data?.unique_visitors || 0
            });
        } catch (error) {
            console.error('Load stats error:', error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        { title: 'Restoranlar', value: stats?.restaurants || 0, icon: '🍽️', color: 'bg-blue-500' },
        { title: 'Kategoriler', value: stats?.categories || 0, icon: '📁', color: 'bg-green-500' },
        { title: 'Ürünler', value: stats?.products || 0, icon: '🍕', color: 'bg-purple-500' },
        { title: 'Kampanyalar', value: stats?.campaigns || 0, icon: '🎯', color: 'bg-red-500' },
        { title: 'Oylamalar', value: stats?.votes || 0, icon: '🗳️', color: 'bg-yellow-500' },
        { title: 'Mesajlar', value: stats?.messages || 0, icon: '💬', color: 'bg-indigo-500', badge: stats?.unreadMessages },
        { title: 'Toplam Görüntüleme', value: stats?.totalHits || 0, icon: '👁️', color: 'bg-pink-500' },
        { title: 'Benzersiz Ziyaretçi', value: stats?.uniqueVisitors || 0, icon: '👤', color: 'bg-teal-500' }
    ];

    return (
        <AdminLayout>
            <div>
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                    
                    {/* Organization Selector */}
                    {organizations.length > 0 && (
                        <select
                            value={selectedOrgId || ''}
                            onChange={(e) => {
                                setSelectedOrgId(e.target.value);
                                navigate(`/admin/organizations/${e.target.value}/dashboard`);
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {organizations.map(org => (
                                <option key={org.id} value={org.id}>
                                    {org.name}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {statCards.map((card, index) => (
                            <div key={index} className="bg-white rounded-lg shadow p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">{card.title}</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                                    </div>
                                    <div className={`${card.color} w-16 h-16 rounded-lg flex items-center justify-center text-3xl`}>
                                        {card.icon}
                                    </div>
                                </div>
                                {card.badge && card.badge > 0 && (
                                    <div className="mt-4">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            {card.badge} okunmamış
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Quick Actions */}
                {selectedOrgId && (
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <a
                            href={`/admin/organizations/${selectedOrgId}/products`}
                            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition"
                        >
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ürün Ekle</h3>
                            <p className="text-sm text-gray-600">Menüye yeni ürün ekleyin</p>
                        </a>
                        <a
                            href={`/admin/organizations/${selectedOrgId}/campaigns`}
                            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition"
                        >
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Kampanya Oluştur</h3>
                            <p className="text-sm text-gray-600">Yeni bir kampanya başlatın</p>
                        </a>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default DashboardPage;

